"use client";

import * as React from "react";
import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { useRender } from "@base-ui/react/use-render";
import { Check, ChevronRight, Circle } from "lucide-react";

import { cn } from "@ergon-ai/ui";

import type { DismissalProps } from "../internal/dismissal";
import type { RadixPositioningProps } from "../internal/positioning";
import { asChildElement, asChildProps } from "../internal/as-child";
import {
  autoFocusProp,
  DismissalProvider,
  useDismissalBridge,
  useRegisterDismissal,
} from "../internal/dismissal";
import { positionerProps } from "../internal/positioning";

type MenuClickEvent = Parameters<
  NonNullable<
    React.ComponentPropsWithoutRef<typeof MenuPrimitive.Item>["onClick"]
  >
>[0];

type SelectEvent = CustomEvent<{ originalEvent: Event }>;

interface SelectableItemProps {
  asChild?: boolean;
  /** Radix's select event; `preventDefault()` keeps the menu open. */
  onSelect?: (event: SelectEvent) => void;
  /** Radix's name for the typeahead text Base UI calls `label`. */
  textValue?: string;
}

/** Radix fired `onSelect` on click and on Enter/Space, and `preventDefault()`
 *  kept the menu open. Base UI has no such prop, so chain it through `onClick`
 *  and turn the cancellation into `preventBaseUIHandler()`. */
function chainOnSelect(
  onSelect: ((event: SelectEvent) => void) | undefined,
  onClick: ((event: MenuClickEvent) => void) | undefined,
) {
  return (event: MenuClickEvent) => {
    onClick?.(event);
    if (!onSelect) return;
    const selectEvent: SelectEvent = new CustomEvent("menu.itemSelect", {
      bubbles: true,
      cancelable: true,
      detail: { originalEvent: event.nativeEvent },
    });
    onSelect(selectEvent);
    if (selectEvent.defaultPrevented) {
      event.preventBaseUIHandler();
    }
  };
}

// Radix's Content defaulted `loop` to false, Base UI's Root defaults
// loopFocus to true. Keep the Radix default and expose the opt-in here: Base
// UI owns the prop on the root, so it can no longer live on the content.
// Tab now leaves the menu and closes it, where Radix swallowed it. That is a
// deliberate keep: APG says Tab moves on, and Radix was the odd one out.
// See EAH-2299.
interface DropdownMenuProps extends React.ComponentPropsWithoutRef<
  typeof MenuPrimitive.Root
> {
  loop?: boolean;
}

const DropdownMenu = ({
  loop = false,
  onOpenChange,
  ...props
}: DropdownMenuProps) => {
  const [register, handleOpenChange] = useDismissalBridge(onOpenChange);
  return (
    <DismissalProvider register={register}>
      <MenuPrimitive.Root
        loopFocus={loop}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DismissalProvider>
  );
};
DropdownMenu.displayName = "DropdownMenu";

// Radix's Portal took `forceMount`; Base UI spells the same thing keepMounted.
const DropdownMenuPortal = ({
  forceMount,
  keepMounted,
  ...props
}: React.ComponentPropsWithoutRef<typeof MenuPrimitive.Portal> & {
  forceMount?: true;
}) => (
  <MenuPrimitive.Portal keepMounted={keepMounted ?? forceMount} {...props} />
);
DropdownMenuPortal.displayName = "DropdownMenuPortal";

const DropdownMenuGroup = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.Group> & {
    asChild?: boolean;
  }
>(({ asChild, children, ...props }, ref) => (
  <MenuPrimitive.Group
    ref={ref}
    {...props}
    {...asChildProps(asChild, children)}
  />
));
DropdownMenuGroup.displayName = "DropdownMenuGroup";

const DropdownMenuRadioGroup = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.RadioGroup>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.RadioGroup> & {
    asChild?: boolean;
  }
>(({ asChild, children, ...props }, ref) => (
  <MenuPrimitive.RadioGroup
    ref={ref}
    {...props}
    {...asChildProps(asChild, children)}
  />
));
DropdownMenuRadioGroup.displayName = "DropdownMenuRadioGroup";

const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.Trigger> & {
    asChild?: boolean;
  }
>(({ asChild, children, ...props }, ref) => (
  <MenuPrimitive.Trigger
    ref={ref}
    {...props}
    {...asChildProps(asChild, children)}
  />
));
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

// Radix closed the whole menu tree on Escape; Base UI defaults to closing
// only the submenu, so keep the Radix behaviour unless a caller overrides.
const DropdownMenuSub = ({
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof MenuPrimitive.SubmenuRoot>) => {
  const [register, handleOpenChange] = useDismissalBridge(onOpenChange);
  return (
    <DismissalProvider register={register}>
      <MenuPrimitive.SubmenuRoot
        closeParentOnEsc
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DismissalProvider>
  );
};
DropdownMenuSub.displayName = "DropdownMenuSub";

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.SubmenuTrigger>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.SubmenuTrigger> & {
    inset?: boolean;
    asChild?: boolean;
    /** Radix's name for the typeahead text Base UI calls `label`. */
    textValue?: string;
  }
>(
  (
    { className, inset, asChild, children, label, textValue, ...props },
    ref,
  ) => (
    <MenuPrimitive.SubmenuTrigger
      ref={ref}
      className={cn(
        "rounded-xs px-2 py-1.5 flex cursor-default items-center text-sm outline-hidden select-none focus:bg-surface-subtle data-popup-open:bg-surface-subtle",
        inset && "pl-8",
        className,
      )}
      label={label ?? textValue}
      {...props}
      {...asChildProps(asChild, children, (content) => (
        <>
          {content}
          <ChevronRight className="size-4 ml-auto" />
        </>
      ))}
    />
  ),
);
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

type DropdownMenuContentProps = React.ComponentPropsWithoutRef<
  typeof MenuPrimitive.Popup
> &
  Pick<
    React.ComponentPropsWithoutRef<typeof MenuPrimitive.Positioner>,
    "align" | "alignOffset" | "side" | "sideOffset"
  > &
  RadixPositioningProps &
  DismissalProps & {
    asChild?: boolean;
    /** Radix's name for Base UI's `keepMounted`; never reaches the DOM. */
    forceMount?: true;
    /** Radix's focus escape hatch; `preventDefault()` leaves focus put. */
    onCloseAutoFocus?: (event: CustomEvent) => void;
  };

function useContentParts({
  align,
  alignOffset,
  side,
  sideOffset,
  collisionBoundary,
  collisionPadding,
  sticky,
  arrowPadding,
  avoidCollisions,
  positionMethod,
  forceMount,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
  ...popup
}: DropdownMenuContentProps) {
  useRegisterDismissal({
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
  });

  return {
    keepMounted: forceMount,
    positioner: {
      align,
      alignOffset,
      side,
      sideOffset,
      ...positionerProps({
        collisionBoundary,
        collisionPadding,
        sticky,
        arrowPadding,
        avoidCollisions,
        positionMethod,
      }),
    },
    popup: { finalFocus: autoFocusProp(onCloseAutoFocus), ...popup },
  };
}

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Popup>,
  DropdownMenuContentProps
>(({ className, asChild, children, ...props }, ref) => {
  const { keepMounted, positioner, popup } = useContentParts({
    align: "start",
    alignOffset: 0,
    side: "right",
    sideOffset: 0,
    ...props,
  });

  return (
    <MenuPrimitive.Portal keepMounted={keepMounted}>
      <MenuPrimitive.Positioner className="isolate z-50" {...positioner}>
        {/* The reset belongs on the popup: that is the element Base UI gives
            tabindex -1 and focuses, so that is where a default ring paints. */}
        <MenuPrimitive.Popup
          ref={ref}
          className={cn(
            "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 p-1 shadow-lg z-50 min-w-[8rem] overflow-hidden rounded-md border bg-surface text-ink outline-none",
            className,
          )}
          {...popup}
          {...asChildProps(asChild, children)}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
});
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Popup>,
  DropdownMenuContentProps
>(({ className, asChild, children, ...props }, ref) => {
  const { keepMounted, positioner, popup } = useContentParts({
    align: "center",
    alignOffset: 0,
    side: "bottom",
    sideOffset: 4,
    ...props,
  });

  return (
    <MenuPrimitive.Portal keepMounted={keepMounted}>
      <MenuPrimitive.Positioner className="isolate z-50" {...positioner}>
        {/* Same here: the reset has to sit on the focusable popup. */}
        <MenuPrimitive.Popup
          ref={ref}
          className={cn(
            "p-1 shadow-md z-50 min-w-[8rem] overflow-hidden rounded-md border bg-surface text-ink outline-none",
            "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
          )}
          {...popup}
          {...asChildProps(asChild, children)}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
});
DropdownMenuContent.displayName = "DropdownMenuContent";

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Item>,
  Omit<React.ComponentPropsWithoutRef<typeof MenuPrimitive.Item>, "onSelect"> &
    SelectableItemProps & { inset?: boolean }
>(
  (
    {
      className,
      inset,
      asChild,
      children,
      onSelect,
      onClick,
      label,
      textValue,
      ...props
    },
    ref,
  ) => (
    <MenuPrimitive.Item
      ref={ref}
      className={cn(
        "rounded-xs px-2 py-1.5 relative flex cursor-default items-center text-sm outline-hidden transition-colors select-none focus:bg-surface-subtle focus:text-ink data-disabled:pointer-events-none data-disabled:opacity-50",
        inset && "pl-8",
        className,
      )}
      label={label ?? textValue}
      onClick={chainOnSelect(onSelect, onClick)}
      {...props}
      {...asChildProps(asChild, children)}
    />
  ),
);
DropdownMenuItem.displayName = "DropdownMenuItem";

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.CheckboxItem>,
  Omit<
    React.ComponentPropsWithoutRef<typeof MenuPrimitive.CheckboxItem>,
    "onSelect"
  > &
    SelectableItemProps
>(
  (
    {
      className,
      children,
      checked,
      asChild,
      onSelect,
      onClick,
      label,
      textValue,
      ...props
    },
    ref,
  ) => (
    <MenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(
        "rounded-xs py-1.5 pl-8 pr-2 relative flex cursor-default items-center text-sm outline-hidden transition-colors select-none focus:bg-surface-subtle focus:text-ink data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      checked={checked}
      label={label ?? textValue}
      // Radix closed the menu when a checkbox item was picked; keep that.
      closeOnClick
      onClick={chainOnSelect(onSelect, onClick)}
      {...props}
      {...asChildProps(asChild, children, (content) => (
        <>
          <span className="left-2 size-3.5 absolute flex items-center justify-center">
            <MenuPrimitive.CheckboxItemIndicator>
              <Check className="size-4" />
            </MenuPrimitive.CheckboxItemIndicator>
          </span>
          {content}
        </>
      ))}
    />
  ),
);
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.RadioItem>,
  Omit<
    React.ComponentPropsWithoutRef<typeof MenuPrimitive.RadioItem>,
    "onSelect"
  > &
    SelectableItemProps
>(
  (
    {
      className,
      children,
      asChild,
      onSelect,
      onClick,
      label,
      textValue,
      ...props
    },
    ref,
  ) => (
    <MenuPrimitive.RadioItem
      ref={ref}
      className={cn(
        "rounded-xs py-1.5 pl-8 pr-2 relative flex cursor-default items-center text-sm outline-hidden transition-colors select-none focus:bg-surface-subtle focus:text-ink data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      label={label ?? textValue}
      // Radix closed the menu when a radio item was picked; keep that.
      closeOnClick
      onClick={chainOnSelect(onSelect, onClick)}
      {...props}
      {...asChildProps(asChild, children, (content) => (
        <>
          <span className="left-2 size-3.5 absolute flex items-center justify-center">
            <MenuPrimitive.RadioItemIndicator>
              <Circle className="size-1.5 fill-current" />
            </MenuPrimitive.RadioItemIndicator>
          </span>
          {content}
        </>
      ))}
    />
  ),
);
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";

// Base UI's GroupLabel throws outside <Menu.Group>; Radix's Label was a
// plain unlabelled div, so render the same plain div for exact parity.
const DropdownMenuLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & {
    inset?: boolean;
    asChild?: boolean;
  }
>(({ className, inset, asChild, children, ...props }, ref) => {
  // Same rule as every other part: the child's own className, ref and
  // handlers are merged in, not replaced.
  const child = asChildElement(asChild, children);
  const labelProps = {
    ...props,
    className: cn("px-2 py-1.5 font-bold text-sm", inset && "pl-8", className),
  };

  return useRender({
    ref,
    render: child ?? <div />,
    props: child ? labelProps : { ...labelProps, children },
  });
});
DropdownMenuLabel.displayName = "DropdownMenuLabel";

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof MenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof MenuPrimitive.Separator> & {
    asChild?: boolean;
  }
>(({ className, asChild, children, ...props }, ref) => (
  <MenuPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-surface-subtle", className)}
    {...props}
    {...asChildProps(asChild, children)}
  />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("tracking-widest ml-auto text-xs opacity-60", className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
